/**
 * Content Loader Utility
 *
 * Loads markdown content files from the CMS (content/pages/ directory).
 * Supports both static file loading and dynamic imports.
 */

export interface PageContent {
  title: string;
  slug: string;
  description?: string;
  body: string;
  seo_title?: string;
  seo_description?: string;
  date?: string;
  draft?: boolean;
  // New fields from enhanced CMS
  hero_image?: string;
  featured_image?: string;
  gallery?: Array<{ image: string; alt?: string; caption?: string }>;
  schema?: Record<string, unknown>;
  custom_scripts_head?: string;
  custom_scripts_footer?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  twitter_title?: string;
  twitter_description?: string;
  twitter_image?: string;
}

const BUNDLED_PAGE_MARKDOWN = import.meta.glob<string>('../../public/content/pages/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const INITIAL_PAGE_CACHE = new Map<string, PageContent>();
for (const [path, raw] of Object.entries(BUNDLED_PAGE_MARKDOWN)) {
  const match = path.match(/\/([^/]+)\.md$/);
  if (match && typeof raw === 'string') {
    INITIAL_PAGE_CACHE.set(match[1], parseMarkdownWithFrontmatter(raw));
  }
}

/**
 * Synchronously return bundled markdown page content for SSR prerender and initial render.
 */
export function getInitialPageContent(slug: string): PageContent | null {
  return INITIAL_PAGE_CACHE.get(slug) ?? null;
}

/**
 * Load a page content file by slug
 * Tries to load from /content/pages/{slug}.md
 */
export async function loadPageContent(slug: string): Promise<PageContent | null> {
  const bundled = getInitialPageContent(slug);
  if (bundled) {
    return bundled;
  }
  try {
    // Content files are served from the public directory
    // Works in both development and production
    const response = await fetch(`/content/pages/${slug}.md`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to load content: ${response.statusText}`);
    }

    const text = await response.text();

    // Parse frontmatter and markdown body
    const parsed = parseMarkdownWithFrontmatter(text);
    return parsed;
  } catch (error) {
    console.error(`Error loading page content for slug "${slug}":`, error);
    return null;
  }
}

/**
 * Load all page content files
 * Returns a list of all available pages
 */
export async function loadAllPages(): Promise<PageContent[]> {
  try {
    // In a real implementation, this would fetch a manifest or index file
    // For now, we'll return an empty array and let pages be loaded individually
    const response = await fetch('/content/pages/index.json');

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.pages || [];
  } catch (error) {
    console.error('Error loading all pages:', error);
    return [];
  }
}

/**
 * Parse markdown file with frontmatter
 * Supports YAML frontmatter at the beginning of the file
 */
function parseMarkdownWithFrontmatter(content: string): PageContent {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    // No frontmatter, treat entire content as body
    return {
      title: '',
      slug: '',
      body: content,
    };
  }

  const frontmatter = match[1];
  const body = match[2];

  // Parse YAML frontmatter (simplified parser)
  const frontmatterObj: Record<string, unknown> = {};
  frontmatter.split('\n').forEach((line) => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Remove quotes if present
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }

      // Parse boolean values
      if (value === 'true') {
        frontmatterObj[key] = true;
      } else if (value === 'false') {
        frontmatterObj[key] = false;
      } else {
        frontmatterObj[key] = value;
      }
    }
  });

  // Parse complex fields (JSON/YAML arrays/objects)
  let gallery: Array<{ image: string; alt?: string; caption?: string }> | undefined;
  let schema: Record<string, unknown> | undefined;

  if (frontmatterObj.gallery) {
    try {
      gallery =
        typeof frontmatterObj.gallery === 'string'
          ? JSON.parse(frontmatterObj.gallery)
          : frontmatterObj.gallery;
    } catch {
      gallery = undefined;
    }
  }

  if (frontmatterObj.schema) {
    try {
      schema =
        typeof frontmatterObj.schema === 'string'
          ? JSON.parse(frontmatterObj.schema)
          : frontmatterObj.schema;
    } catch {
      schema = undefined;
    }
  }

  const fm = (key: string) => {
    const v = frontmatterObj[key];
    return typeof v === 'string' ? v : undefined;
  };
  const fmBool = (key: string) => frontmatterObj[key] === true || frontmatterObj[key] === 'true';

  return {
    title: fm('title') || '',
    slug: fm('slug') || '',
    description: fm('description'),
    body: body.trim(),
    seo_title: fm('seo_title'),
    seo_description: fm('seo_description'),
    date: fm('date'),
    draft: fmBool('draft'),
    // New fields
    hero_image: fm('hero_image'),
    featured_image: fm('featured_image'),
    gallery,
    schema,
    custom_scripts_head: fm('custom_scripts_head'),
    custom_scripts_footer: fm('custom_scripts_footer'),
    og_title: fm('og_title'),
    og_description: fm('og_description'),
    og_image: fm('og_image'),
    twitter_title: fm('twitter_title'),
    twitter_description: fm('twitter_description'),
    twitter_image: fm('twitter_image'),
  };
}

/**
 * Check if a page exists
 */
export async function pageExists(slug: string): Promise<boolean> {
  const content = await loadPageContent(slug);
  return content !== null && !content.draft;
}
